import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import css from './ExerciseDetail.module.css';

interface MonthlyRow {
  month: string;
  sets: number;
  tonnage_kg: number | null;
  best_set_weight: number | null;
  best_est_1rm: number | null;
  total_distance_m: number | null;
  total_duration_sec: number | null;
}

interface RecentSet {
  date: string;
  completed_at: number;
  weight_kg: number | null;
  reps: number | null;
  rpe: number | null;
  duration_sec: number | null;
  distance_m: number | null;
  speed_kmh: number | null;
}

interface BestStrength {
  heaviest_set: { weight_kg: number; reps: number; rpe: number | null; date: string } | null;
  est_1rm: { value: number; weight_kg: number; reps: number; date: string } | null;
}

interface BestCardio {
  longest_duration: { duration_sec: number; distance_m: number | null; date: string } | null;
  longest_distance: { distance_m: number; duration_sec: number | null; date: string } | null;
}

export interface ExerciseDetailData {
  id: string;
  name: string | null;
  region: string | null;
  total_sets: number;
  total_sessions: number;
  first_seen: string;
  last_seen: string;
  best_strength: BestStrength;
  best_cardio: BestCardio;
  monthly: MonthlyRow[];
  recent_sets: RecentSet[];
}

export function ExerciseDetail({ data }: { data: ExerciseDetailData }) {
  const isStrength = data.monthly.some((m) => m.best_est_1rm != null);
  const isCardio = data.monthly.some((m) => m.total_distance_m != null || m.total_duration_sec != null);

  const labelFormatter = useMemo(
    () => (v: string) => {
      const [y, m] = v.split('-').map(Number);
      if (!y || !m) return v;
      const month = new Date(y, m - 1, 1).toLocaleString('en', { month: 'short' });
      return m === 1 ? `${month} ${String(y).slice(-2)}` : month;
    },
    [],
  );

  return (
    <div>
      <div className={css.header}>
        <div>
          <h3 className={css.exName}>{data.name ?? data.id}</h3>
          <p className={css.exMeta}>
            {data.region && <span>{data.region} · </span>}
            <span>{data.total_sets.toLocaleString()} sets across {data.total_sessions.toLocaleString()} sessions</span>
            <span className={css.dim}> · {data.first_seen} → {data.last_seen}</span>
          </p>
        </div>
      </div>

      <div className={css.prCards}>
        {data.best_strength.heaviest_set && (
          <PrCard
            label="Heaviest set"
            primary={`${data.best_strength.heaviest_set.weight_kg} kg × ${data.best_strength.heaviest_set.reps}`}
            secondary={`${data.best_strength.heaviest_set.date}${data.best_strength.heaviest_set.rpe != null ? ` · RPE ${data.best_strength.heaviest_set.rpe}` : ''}`}
          />
        )}
        {data.best_strength.est_1rm && (
          <PrCard
            label="Best est 1RM"
            primary={`${data.best_strength.est_1rm.value} kg`}
            secondary={`from ${data.best_strength.est_1rm.weight_kg} × ${data.best_strength.est_1rm.reps} on ${data.best_strength.est_1rm.date}`}
          />
        )}
        {data.best_cardio.longest_distance && (
          <PrCard
            label="Longest distance"
            primary={`${(data.best_cardio.longest_distance.distance_m / 1000).toFixed(2)} km`}
            secondary={`${data.best_cardio.longest_distance.date}`}
          />
        )}
        {data.best_cardio.longest_duration && (
          <PrCard
            label="Longest duration"
            primary={fmtDuration(data.best_cardio.longest_duration.duration_sec)}
            secondary={`${data.best_cardio.longest_duration.date}`}
          />
        )}
      </div>

      {isStrength && (
        <div className={css.charts}>
          <ChartBlock title="Estimated 1RM over time (Epley)">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={data.monthly.filter((m) => m.best_est_1rm != null)}
                margin={{ top: 8, right: 12, left: -12, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                <XAxis dataKey="month" stroke="#666" fontSize={11} tickFormatter={labelFormatter} minTickGap={20} />
                <YAxis stroke="#666" fontSize={11} tickFormatter={(v: number) => `${v}kg`} />
                <Tooltip
                  cursor={{ stroke: '#444', strokeWidth: 1 }}
                  contentStyle={{ background: '#0f0f0f', border: '1px solid #2a2a2a', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: '#ddd' }}
                  formatter={(v: number | string) => [`${v} kg`, 'est 1RM']}
                />
                <Line
                  type="monotone"
                  dataKey="best_est_1rm"
                  stroke="#4ade80"
                  strokeWidth={2}
                  dot={{ fill: '#4ade80', r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartBlock>

          <ChartBlock title="Tonnage per month">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.monthly} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                <XAxis dataKey="month" stroke="#666" fontSize={11} tickFormatter={labelFormatter} minTickGap={20} />
                <YAxis stroke="#666" fontSize={11} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}t`} />
                <Tooltip
                  cursor={{ fill: '#1a1a1a' }}
                  contentStyle={{ background: '#0f0f0f', border: '1px solid #2a2a2a', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: '#ddd' }}
                  formatter={(v: number | string) => [`${Number(v).toLocaleString()} kg`, 'tonnage']}
                />
                <Bar dataKey="tonnage_kg" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </ChartBlock>
        </div>
      )}

      {isCardio && (
        <div className={css.charts}>
          <ChartBlock title="Distance per month">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.monthly} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                <XAxis dataKey="month" stroke="#666" fontSize={11} tickFormatter={labelFormatter} minTickGap={20} />
                <YAxis
                  stroke="#666"
                  fontSize={11}
                  tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}km`}
                />
                <Tooltip
                  cursor={{ fill: '#1a1a1a' }}
                  contentStyle={{ background: '#0f0f0f', border: '1px solid #2a2a2a', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: '#ddd' }}
                  formatter={(v: number | string) => [
                    `${(Number(v) / 1000).toFixed(2)} km`,
                    'distance',
                  ]}
                />
                <Bar dataKey="total_distance_m" fill="#f97316" />
              </BarChart>
            </ResponsiveContainer>
          </ChartBlock>
        </div>
      )}

      <div className={css.recent}>
        <h4 className={css.recentTitle}>Last {data.recent_sets.length} sets</h4>
        <table className={css.recentTable}>
          <thead>
            <tr>
              <th>Date</th>
              <th className={css.thNum}>{isStrength ? 'Weight' : 'Distance'}</th>
              <th className={css.thNum}>{isStrength ? 'Reps' : 'Duration'}</th>
              <th className={css.thNum}>{isStrength ? 'RPE' : 'Speed'}</th>
              <th className={css.thNum}>{isStrength ? 'Est 1RM' : ''}</th>
            </tr>
          </thead>
          <tbody>
            {data.recent_sets.map((s, i) => (
              <tr key={i}>
                <td>{s.date}</td>
                <td className={css.num}>
                  {isStrength
                    ? s.weight_kg != null
                      ? `${s.weight_kg} kg`
                      : 'BW'
                    : s.distance_m != null
                      ? `${(s.distance_m / 1000).toFixed(2)} km`
                      : '—'}
                </td>
                <td className={css.num}>
                  {isStrength
                    ? (s.reps ?? '—')
                    : s.duration_sec != null
                      ? fmtDuration(s.duration_sec)
                      : '—'}
                </td>
                <td className={css.num}>
                  {isStrength
                    ? (s.rpe ?? '—')
                    : s.speed_kmh != null
                      ? `${s.speed_kmh.toFixed(1)} km/h`
                      : '—'}
                </td>
                <td className={css.num}>
                  {isStrength && s.weight_kg != null && s.reps != null
                    ? `${Math.round(s.weight_kg * (1 + s.reps / 30) * 10) / 10} kg`
                    : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PrCard({
  label,
  primary,
  secondary,
}: {
  label: string;
  primary: string;
  secondary: string;
}) {
  return (
    <div className={css.prCard}>
      <div className={css.prLabel}>{label}</div>
      <div className={css.prPrimary}>{primary}</div>
      <div className={css.prSecondary}>{secondary}</div>
    </div>
  );
}

function ChartBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className={css.chartBlock}>
      <div className={css.chartTitle}>{title}</div>
      {children}
    </div>
  );
}

function fmtDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
